/**
 * ShootingScheduling — one container for the question "when does the shoot
 * happen, and who does it", as the logistics prototype frames it
 * (`poc:.pf-schedule`, purchase-flow-logistics-v3).
 *
 * It gathers up three things the Instructions step used to ask separately: the
 * favourite-creative picker, and the date fields inside the on-site and
 * key-pickup meeting cards. Asked separately they read as three
 * unrelated forms; the client is making one decision.
 *
 * **It is not a dialog.** The prototype opens fixed booking in a modal, and this
 * deliberately does not — the client is scheduling against an order whose
 * products, address and total are on the page, and a dialog removes exactly
 * those at the moment they matter. The panel renders under the two cards, the
 * page stays whole, and only the panel's contents change with the choice.
 *
 * The calendar offers every future day; no creative's real availability is read yet.
 */

import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined'
import EventRepeatOutlined from '@mui/icons-material/EventRepeatOutlined'
import { Moment } from 'moment-timezone'
import { useTranslation } from 'react-i18next'

import { BEIGE_500, BEIGE_600, BLUE_300, GRAY_700, GRAY_900, SELECTED_BLUE, WHITE } from 'constants/styling/theme'
import type { RevampCreative } from 'dataQueries/purchaseFlowRevampCreatives.query'


import { FixedBookingPanel } from './FixedBookingPanel.component'
import { FlexibleBookingPanel } from './FlexibleBookingPanel.component'
import { SchedulePanel } from './SchedulePanel.component'
import type { ScheduleMethod } from './scheduleMethod'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'

/** The method's glyph in a round beige well. */
const MethodIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ width: '4.6rem', height: '4.6rem', borderRadius: '50%', backgroundColor: BEIGE_500, color: GRAY_700 }}>
    {children}
  </Stack>
)

const MethodLabel: React.FC<{ title: string, description: string }> = ({ title, description }) => (
  <Stack gap={0.2}>
    <Typography variant="text-md" fontWeight={550} color={GRAY_900} sx={{ lineHeight: 1.4 }}>{title}</Typography>
    <Typography variant="text-sm" color={GRAY_700} sx={{ lineHeight: 1.4 }}>{description}</Typography>
  </Stack>
)

interface Props {
  method?: ScheduleMethod
  onPickMethod: (method: ScheduleMethod) => void
  day?: Moment | null
  onPickDay: (day: Moment | null) => void
  hour?: number
  minute?: number
  onPickHour: (hour: number) => void
  onPickMinute: (minute: number | undefined) => void
  /** Whether the client may pick their creative — a paid-plan feature. Without
   *  it the panels offer the date alone. */
  canChooseCreative: boolean
  choosingCreative: boolean
  onToggleChoosingCreative: (next: boolean) => void
  creatives: ReadonlyArray<RevampCreative>
  creativeId?: string
  onPickCreative: (id: string) => void
  isCreativesLoading?: boolean
  /** The last list came back empty, so the toggle switched itself off. */
  noCreativesAvailable?: boolean
  minDateTime?: Moment
  timezone?: string
  userTimezone?: string
  /** The completed pick, ISO — what the order is placed with. */
  scheduledAt?: string
}

export const ShootingScheduleSection: React.FC<Props> = ({
  method,
  onPickMethod,
  day,
  onPickDay,
  hour,
  minute,
  onPickHour,
  onPickMinute,
  canChooseCreative,
  choosingCreative,
  onToggleChoosingCreative,
  creatives,
  creativeId,
  onPickCreative,
  isCreativesLoading = false,
  noCreativesAvailable = false,
  minDateTime,
  timezone,
  userTimezone,
  scheduledAt,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  return (
    <SectionedBorderBox variant="flat"
      centeredTitle
      backgroundColor={BEIGE_500}
      borderColor={undefined}
      border="none"
      title={t('purchase_flow_revamp:instructions.schedule.section_title')}
      data-testid="pf-revamp-shooting-schedule"
    >

      {/* Until a booking method is picked the access and date fields are not shown, so this choice is the gap. */}
      <Box data-pf-gap={method ? undefined : 'meeting_type_missing meeting_date_invalid'}>
      <SelectableButtonsGrid<ScheduleMethod>
        direction={{ xs: 'column', sm: 'row' }}
        equalWidth
        layout="vertical"
        selectionRole="radio"
        ariaLabel={t('purchase_flow_revamp:instructions.schedule.section_title')}
        gap={0.8}
        selectedValue={method}
        onSelect={onPickMethod}
        sx={{
          height: 'auto',
          p: 2.4,
          // Doubled to outrank the grid's own `.selectableGrid .selectableGridButton.active` rules.
          '&&&': { backgroundColor: WHITE, borderColor: BEIGE_600 },
          '&&&:hover': { backgroundColor: WHITE, borderColor: SELECTED_BLUE },
          '&&&[aria-checked="true"]': { backgroundColor: WHITE, borderColor: SELECTED_BLUE, boxShadow: `0 0 0 3px ${BLUE_300}` },
        }}
        values={[
          {
            value: 'fixed',
            icon: <MethodIcon><EventAvailableOutlined sx={{ fontSize: '2.4rem' }} /></MethodIcon>,
            displayName: <MethodLabel title={t('purchase_flow_revamp:instructions.schedule.fixed_title')} description={t('purchase_flow_revamp:instructions.schedule.fixed_description')} />,
            dataCy: 'pf-revamp-schedule-method-fixed',
          },
          {
            value: 'flexible',
            icon: <MethodIcon><EventRepeatOutlined sx={{ fontSize: '2.4rem' }} /></MethodIcon>,
            displayName: <MethodLabel title={t('purchase_flow_revamp:instructions.schedule.flexible_title')} description={t('purchase_flow_revamp:instructions.schedule.flexible_description')} />,
            dataCy: 'pf-revamp-schedule-method-flexible',
          },
        ]}
      />
      </Box>


      {method === 'fixed' &&
        <SchedulePanel dataCy="pf-revamp-schedule-panel-fixed">
          <FixedBookingPanel
            day={day}
            onPickDay={onPickDay}
            hour={hour}
            minute={minute}
            onPickHour={onPickHour}
            onPickMinute={onPickMinute}
            canChooseCreative={canChooseCreative}
            choosingCreative={choosingCreative}
            onToggleChoosingCreative={onToggleChoosingCreative}
            creatives={creatives}
            creativeId={creativeId}
            onPickCreative={onPickCreative}
            isCreativesLoading={isCreativesLoading}
            noCreativesAvailable={noCreativesAvailable}
            minDateTime={minDateTime}
            timezone={timezone}
            userTimezone={userTimezone}
            scheduledAt={scheduledAt}
          />
        </SchedulePanel>
      }

      {method === 'flexible' &&
        <SchedulePanel dataCy="pf-revamp-schedule-panel-flexible">
          <FlexibleBookingPanel
            canChooseCreative={canChooseCreative}
            choosingCreative={choosingCreative}
            onToggleChoosingCreative={onToggleChoosingCreative}
            creatives={creatives}
            creativeId={creativeId}
            onPickCreative={onPickCreative}
            isCreativesLoading={isCreativesLoading}
            noCreativesAvailable={noCreativesAvailable}
          />
        </SchedulePanel>
      }

    </SectionedBorderBox>
  )
}
